import Vue from 'vue';
import VueRouter from 'vue-router';

import store from '@/store/store';
import Dashboard from '@/views/Dashboard';
import { RootActions } from '@/store/types/action-types';
import { LayoutEnum } from '@/bundles/App/enums';

import Welcome from '@/components/Welcome';
import AccountLayout from '@/bundles/App/components/AccountLayout';

import MarketingRoutes from './routes/marketing-routes';
import ReportRoutes from './routes/report-routes';
import MarketingTemplatesRoutes from './routes/marketing-templates-routes';
import ClientRoutes from './routes/client-routes';
import {
  redirectHandler,
  requiresTokenGuard,
  requiresRedirectGuard,
  requiresAdminTokenGuard,
  requiresTokenAbsenceGuard,
  requiresSuperAdminRoleGuard,
  requiresFinancialAdminRoleGuard,
  requiresTokenOrTemporaryTokenGuard,
  requiresFinancialAdminOrAdminRoleGuard,
  requireRegularUser,
  requiresFinancialOrMarketingAdminRoleGuard,
  requiresAdminRoleGuard,
  requiresPasswordChangeGuard,
} from './guards';

// lazy routes
const ReportDetail = () => import('@/views/Reports/ReportDetail')
const CompaniesMergeWindow = () => import('@/components/companies/CompaniesMergeWindow')
const SavedSets = () => import('@/views/SavedSet/SavedSets')
const PipelinesNew = () => import('@/views/Pipeline/PipelinesNew')
const OccupiersNew = () => import('@/views/Occupier/OccupiersNew')
const RequirementsNew = () => import('@/views/Requirement/RequirementsNew')
const ProjectsNew = () => import('@/views/Project/ProjectsNew')
const SignUp = () => import('@/components/SignUp')
const ConfirmAccount = () => import('@/components/ConfirmAccount')
const Register = () => import('@/components/Register')
const CreateAccount = () => import('@/components/CreateAccount')
const AdministrationRootView = () => import('@/bundles/Administration/components/RootView/AdministrationRootView.vue');
const Administration = () => import('@/components/account/administration')
const CompaniesNew = () => import('@/views/Company/CompaniesNew')
const Properties = () => import('@/views/Property/Properties')

// Availabilities
const Availability = () => import('@/views/Availability/Availability')
const AvailabilityForm = () => import('@/components/availabilities/AvailabilityForm')
const AvailabilityEditForm = () => import('@/components/availabilities/AvailabilityEditForm')
const Availabilities = () => import('@/views/Availability/Availabilities')

const ProjectCard = () => import('@/views/Project/ProjectNew')
const Occupier = () => import('@/views/Occupier/OccupierNew')
const OccupierMergeView = () => import('@/bundles/Occupier/views/merge/OccupierMergeView.vue');
const Requirement = () => import('@/views/Requirement/RequirementNew')
const Pipeline = () => import('@/views/Pipeline/PipelineNew')
const Property = () => import('@/views/Property/Property')
const PropertyMergeView = () => import('@/bundles/Property/views/merge/PropertyMergeView.vue');
const Company = () => import('@/views/Company/CompanyNew')
const AccessDenied = () => import('@/components/AccessDenied')

// new public functions
const PublicMailchimpAvailability = () => import(/* webpackChunkName: "availability-public" */ '@/views/Public/Availability')
const PublicAvailability = () => import(/* webpackChunkName: "availability-public" */ '@/views/Public/Availability')

// Contacts
const ContactsNew = () => import(/* webpackChunkName: "contacts" */ '@/views/Contact/ContactsNew')
const Contact = () => import(/* webpackChunkName: "contact-record" */ '@/views/Contact/ContactNew')
const ContactsMergeWindow = () => import(/* webpackChunkName: "import-contacts" */'@/components/contacts/ContactsMergeWindow')

// Comparables
const ComparablesNew = () => import(/* webpackChunkName: "comparables" */ '@/views/Comparable/ComparablesNew')
const Comparable = () => import(/* webpackChunkName: "comparable" */ '@/views/Comparable/ComparableNew')
const ComparablePrint = () => import(/* webpackChunkName: "comparable-print" */ '@/views/Comparable/ComparablePrint')
const ComparableReport = () => import(/* webpackChunkName: "comparable" */ '@/views/Comparable/ComparableReport')

// Deals
const Deal = () => import('@/views/Deals/DealNew')
const DealsNew = () => import('@/views/Deals/DealsNew')
const CreateDeal = () => import('@/views/Deals/DealsCreate')
const DealPrint = () => import('@/views/Deals/DealPrint')
const DealInvoice = () => import('@/components/invoice/InvoicePreview')

// Spatial
const SpatialSearch = () => import('@/components/spatial/SpatialSearch')

// Public Email Preferences
const EmailPreferences = () => import('@/views/Preferences/EmailPreferences')

// User Settings
const UserSettingsRootViewNew = () => import('@/views/Account/RootViewNew')
const UserProfile = () => import('@/components/account/ProfileInfoForm')
const UserSecurity = () => import('@/views/Account/Security')
const UserPermissions = () => import('@/views/Account/Permissions')
const UserSettings = () => import('@/views/Account/Settings')
const UserSubscription = () => import('@/views/Account/Subscription')
const UserMain = () => import('@/bundles/Settings/views/Main.vue')
const UserApps = () => import('@/views/Account/Apps')
const MagicLinkAuth = () => import('@/bundles/Auth/views/MagicLinkAuth');
const ResetPassword = () => import('@/bundles/Auth/views/ResetPassword');
const DashboardOrder = () => import('@/bundles/Settings/components/DashboardOrder.vue');
const NotificationsSettings = () => import('@/bundles/Settings/components/NotificationsSettings.vue');
const OpexSettings = () => import('@/bundles/Settings/components/OpexSettings.vue');
const VisualizationSettings = () => import('@/bundles/Settings/components/VisualizationSettings.vue');
const PermissionSettings = () => import('@/bundles/Settings/components/PermissionSettings.vue');
const FocusAreaSettings = () => import('@/bundles/Settings/components/FocusAreaSettings.vue');

const Unfollow = () => import('@/views/Unfollow')
const ThanksSignIn = () => import('@/views/ThanksSignIn')

// Members
const Members = () => import('@/views/Member/Members')

// Marketing
const MarketingDashboard = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/MarketingDashboard')
const PressReleases = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/PressReleases');
const PressRelease = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/PressRelease')
const ProcessPressRelease = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/ProcessPressRelease')

const AccountProviderList = () => import(/* webpackChunkName: "account-provider" */ '@/bundles/AccountProvider/views/AccountProviderList.vue');

// OAuth
const OAuthAuthorize = () => import('@/bundles/OAuth/views/OAuthAuthorize');

// account-routes
const MyAccounts = () => import(/* webpackChunkName: "account-routes" */ '@/bundles/Account/views/MyAccounts.vue');

const routes = [
  {
    path: '/pub/availability/:hash',
    component: PublicAvailability,
    meta: {
      title: 'Availability',
    },
    props: true,
  },
  {
    path: '/pub/:provider/availability/:hash/:mcCampaign/:mcContact',
    component: PublicMailchimpAvailability,
    meta: {
      title: 'Availability',
    },
    props: true,
  },
  {
    path: '/',
    name: 'welcome',
    component: Welcome,
    meta: {
      title: 'Welcome',
      requiresTokenAbsence: true
    },
  },
  {
    path: '/signin',
    name: 'signin',
    component: Welcome,
    meta: {
      title: 'Sign In',
      requiresTokenAbsence: true
    },
  },
  {
    path: '/signup',
    component: SignUp,
    meta: {
      title: 'Sign Up',
    },
  },
  {
    path: '/register',
    component: Register,
    meta: {
      title: 'Register',
    },
  },
  {
    path: '/goodbye',
    component: Welcome,
    meta: {
      title: 'Goodbye',
    },
  },
  {
    path: '/create-account',
    component: CreateAccount,
    meta: {
      title: 'Create Account',
    },
  },
  {
    path: '/confirm-account',
    component: ConfirmAccount,
    meta: {
      title: 'Confirm Account',
    },
  },
  {
    path: '/order-status',
    name: 'order-status',
    component: () => import('@/components/workorder/OrderStatus.vue'),
    meta: {
      title: 'Order Status',
    },
  },
  {
    path: '/preferences',
    component: EmailPreferences,
    meta: {
      title: 'Preferences',
    },
  },
  {
    component: Unfollow,
    path: '/unfollow',
    meta: {
      title: 'Unfollow',
    },
    props: true,
  },
  {
    name: 'ThanksPage',
    path: '/sign-in/confirmation',
    meta: {
      title: 'Thanks',
    },
    component: ThanksSignIn,
  },
  {
    name: 'forgot-password',
    path: '/forgot-password',
    meta: {
      title: 'Forgot Password',
    },
    component: MagicLinkAuth,
  },
  {
    name: 'reset-password',
    path: '/reset-password',
    meta: {
      title: 'Reset Password',
      public: true,
    },
    component: ResetPassword,
  },
  {
    path: '/my-accounts',
    name: 'my-accounts',
    component: MyAccounts,
    meta: {
      title: 'My Accounts',
      requiresTokenOrTemporaryToken: true
    },
  },
  {
    path: '/404',
    name: '404', // :key="$route.fullPath" removed
    meta: {
      title: '404',
    },
    component: () => import('@/views/NotFoundView.vue'),
  },
  {
    path: '/oauth/authorize',
    name: 'oauth-authorize',
    component: OAuthAuthorize,
    meta: {
      title: 'Authorize Application',
      requiresToken: true,
    },
  },
  {
    path: '/status',
    name: 'system-status',
    component: () => import('@/bundles/SystemStatus/views/SystemStatusPage.vue'),
    meta: { title: 'System Status', public: true },
  },
  // all routes without accountId should be defined above that
  {
    path: '/:accountId',
    component: AccountLayout,
    meta: {
      requiresToken: true
    },
    children: [
      {
        path: '', // handling routes if they have no account id
        meta: {
          layout: LayoutEnum.Blank,
        },
        beforeEnter (to, from, next) {
          redirectHandler(to, next, store, true);
        }
      },
      {
        path: 'dashboard',
        name: 'dashboard', // :key="$route.fullPath" removed
        component: Dashboard,
        meta: {
          title: 'Dashboard',
        },
      },
      {
        path: 'pipelines-dashboard',
        name: 'pipelines-dashboard',
        component: () => import('@/bundles/Pipeline/views/PipelineDashboardPage.vue'),
        meta: {
          title: 'Pipeline Dashboard',
        },
      },
      {
        path: 'deals-dashboard',
        name: 'deals-dashboard',
        component: () => import('@/bundles/Deals/views/DealsDashboardPage.vue'),
        meta: {
          title: 'Deals Dashboard',
        },
      },
      {
        path: 'availabilities-dashboard',
        name: 'availabilities-dashboard',
        component: () => import('@/bundles/Availabilities/views/AvailabilitiesDashboardPage.vue'),
        meta: {
          title: 'Availabilities Dashboard',
        },
      },
      {
        path: 'comparables-dashboard',
        name: 'comparables-dashboard',
        component: () => import('@/bundles/Comparables/views/ComparablesDashboardPage.vue'),
        meta: {
          title: 'Comparables Dashboard',
        },
      },
      {
        path: 'client',
        component: () => import('@/bundles/App/layouts/AppClientLayout.vue'),
        meta: {
          title: 'Dashboard',
          layout: LayoutEnum.Blank, // AppClientLayout brings its own bars
          allowClientAccess: true,
        },
        children: [
          ...ClientRoutes,
        ],
      },
      {
        path: 'no-access',
        name: 'no-access',
        component: () => import('@/bundles/Account/views/NoAccessView.vue'),
        meta: {
          title: 'No Access',
        },
      },
      {
        path: 'properties',
        name: 'properties', // :key="$route.fullPath" removed
        component: Properties,
        meta: {
          title: 'Properties',
        },
      },
      {
        path: 'property/:id',
        name: 'property',
        component: Property,
        meta: {
          title: 'Property',
        },
        props: true,
      },
      {
        path: 'property-merge/:id',
        name: 'property-merge',
        component: PropertyMergeView,
        meta: {
          title: 'Property Merge',
        },
      },
      {
        path: 'contacts',
        name: 'contacts', // :key="$route.fullPath" removed
        component: ContactsNew,
        meta: {
          title: 'Contacts',
        },
      },
      {
        path: 'availabilities',
        component: Availabilities,
        name: 'availabilities', // :key="$route.fullPath" removed
        meta: {
          title: 'Availabilities',
        },
      },
      {
        name: 'availability', // :key="$route.fullPath" removed
        path: 'availability/:id',
        component: Availability,
        meta: {
          title: 'Availability',
        },
      },
      {
        path: 'availability/create',
        name: 'availability-create',
        component: AvailabilityForm,
        props: true,
        meta: {
          title: 'Availability Create',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'availability/edit/:id/:type',
        component: AvailabilityEditForm,
        name: 'availability-edit',
        meta: {
          title: 'Availability Edit',
          layout: LayoutEnum.Blank,
        },
        props: true,
      },
      {
        path: 'projects',
        name: 'projects', // :key="$route.fullPath" removed
        component: ProjectsNew,
        meta: {
          title: 'Projects',
        },
      },
      {
        path: 'project/:id',
        name: 'project',
        component: ProjectCard,
        meta: {
          title: 'Project',
        },
        props: true,
      },
      {
        path: 'comparables',
        component: ComparablesNew,
        name: 'comparables', // :key="$route.fullPath" removed
        meta: {
          title: 'Comparables',
        },
      },
      {
        path: '/comparables/report',
        component: ComparableReport,
        name: 'comparables-report',
        meta: {
          title: 'Comparable Report',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'comparables/pdf-report',
        component: () => import('@/views/Reports/ReportPDF'),
        name: 'comparable-pdf-report',
        meta: {
          title: 'Comparable PDF Report',
        },
      },
      {
        path: 'occupiers',
        component: OccupiersNew,
        name: 'occupiers', // :key="$route.fullPath" removed
        meta: {
          title: 'Occupiers',
        },
      },
      {
        path: 'occupier/:id',
        props: true,
        name: 'occupier',
        meta: {
          title: 'Occupier',
        },
        component: Occupier,
      },
      {
        path: 'occupier-merge/:id',
        name: 'occupier-merge',
        component: OccupierMergeView,
        meta: {
          title: 'Occupier Merge',
        },
      },
      {
        path: 'requirements',
        name: 'requirements', // :key="$route.fullPath" removed
        component: RequirementsNew,
        meta: {
          title: 'Requirements',
        },
      },
      {
        path: 'requirement/:id',
        props: true,
        name: 'requirement',
        component: Requirement,
        meta: {
          title: 'Requirement',
        },
      },
      {
        path: 'pipelines',
        name: 'pipelines', // :key="$route.fullPath" removed
        component: PipelinesNew,
        meta: {
          title: 'Pipelines',
        },
      },
      {
        path: 'pipeline/:id',
        component: Pipeline,
        name: 'pipeline',
        props: true,
        meta: {
          title: 'Pipeline',
        },
      },
      {
        path: 'deals',
        component: DealsNew,
        name: 'deals', // :key="$route.fullPath" removed
        meta: {
          title: 'Deals',
        },
      },
      {
        path: 'deal/:id',
        component: Deal,
        name: 'deal',
        meta: {
          title: 'Deal',
        },
      },
      {
        path: 'deal/:id/print',
        name: 'deal-print',
        component: DealPrint,
        meta: {
          title: 'Deal Print',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'deals-create',
        component: CreateDeal,
        name: 'deal-create',
        meta: {
          title: 'Deal Create',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'deal/:id/invoice',
        name: 'deal-invoice',
        component: DealInvoice,
        meta: {
          title: 'Deal Invoice',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'spatial-gis',
        component: SpatialSearch,
        name: 'spatial-search', // :key="$route.fullPath" removed
        meta: {
          title: 'Spatial GIS',
        },
      },
      {
        path: 'contact/:id',
        name: 'contact',
        component: Contact,
        props: true,
        meta: {
          title: 'Contact',
        },
      },
      {
        path: 'contact/:id/merge',
        name: 'contact-merge',
        component: ContactsMergeWindow,
        props: true,
        meta: {
          title: 'Contacts Merge',
        },
      },
      {
        path: 'companies',
        component: CompaniesNew,
        name: 'companies', // :key="$route.fullPath" removed
        props: true,
        meta: {
          title: 'Companies',
        },
      },
      {
        path: 'company/:id',
        name: 'company',
        component: Company,
        props: true,
        meta: {
          title: 'Company',
        },
      },
      {
        path: 'company/:id/merge',
        name: 'company-merge',
        component: CompaniesMergeWindow,
        props: true,
        meta: {
          title: 'Companies Merge',
        },
      },
      {
        path: 'report/:id/system',
        name: 'ReportDetail',
        component: ReportDetail,
        props: true,
        meta: {
          title: 'Report Detail',
        },
      },
      ...MarketingTemplatesRoutes,
      {
        path: 'administration',
        component: Administration,
        children: [
          {
            path: '',
            name: 'administration-root-page',
            meta: {
              title: 'Administration',
            },
            component: AdministrationRootView,
          },
          {
            path: 'member/:id', // removed :key="$route.fullPath" for all children
            component: () => import('@/views/Member/MemberRoot.vue'),
            children: [
              {
                path: '',
                name: 'member-account', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member Account',
                  requiresFinancialAdminOrAdminRole: true,
                },
                component: () => import('@/views/Member/MemberNew.vue'),
              },
              {
                path: 'notes',
                name: 'member-notes', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member Notes',
                  requiresFinancialAdminOrAdminRole: true,
                },
                component: () => import('@/views/Member/MemberNotes.vue'),
              },
              {
                path: 'hr',
                name: 'member-hr', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member HR',
                  requiresFinancialAdminOrAdminRole: true,
                },
                component: () => import('@/views/Member/MemberHR.vue'),
              },
              {
                path: 'security',
                name: 'member-security', // :key="$route.fullPath" removed
                component: () => import('@/views/Member/MemberSecurity.vue'),
                meta: {
                  title: 'Member Security',
                  requiresSuperAdminRole: true
                },
              },
              {
                path: 'company-details',
                name: 'member-company-details', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member Company Details',
                  requiresFinancialAdminOrAdminRole: true,
                },
                component: () => import('@/views/Member/MemberCompanyDetails.vue'),
              },
              {
                path: 'license-info',
                name: 'member-license-info', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member License Info',
                  requiresFinancialAdminOrAdminRole: true,
                  // TODO sychdo: handle adminToken guard
                  // requiresAdminToken: true
                },
                component: () => import('@/views/Member/MemberLicense.vue'),
                beforeEnter (to, from, next) {
                  if ((store.state.adminToken && (new Date().getTime() < store.state.adminTokenExpiresTime)) || to.query.showLogin) {
                    next()
                  } else {
                    next(`${to.path}?showLogin=true`)
                  }
                },
              },
              {
                path: 'compensation',
                name: 'member-compensation', // :key="$route.fullPath" removed
                component: () => import('@/views/Member/MemberCompensation'),
                meta: {
                  title: 'Member Compensation',
                  requiresFinancialAdminRole: true,
                  // TODO sychdo: handle adminToken guard
                  // requiresAdminToken: true
                },
                beforeEnter (to, from, next) {
                  if ((store.state.adminToken && (new Date().getTime() < store.state.adminTokenExpiresTime)) || to.query.showLogin) {
                    next()
                  } else {
                    next(`${to.path}?showLogin=true`)
                  }
                },
              },
              {
                path: 'documents',
                name: 'member-documents', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member Documents',
                  requiresSuperAdminRole: true
                },
                component: () => import('@/views/Member/MemberDocuments.vue'),
              },
              {
                path: 'login',
                name: 'member-login', // :key="$route.fullPath" removed
                meta: {
                  title: 'Member Login',
                  requiresSuperAdminRole: true,
                },
                component: () => import('@/views/Member/MemberLogin.vue'),
              },
            ]
          },
          {
            path: 'marketing-details',
            name: 'marketing-root',
            meta: {
              title: 'Marketing Details',
              requiresFinancialOrMarketingAdminRole: true,
            },
            component: () => import('@/views/Marketing/CompanyDetails.vue'),
          },
          {
            path: 'marketing-directives',
            name: 'marketing-directives',
            meta: {
              title: 'Marketing Missions & Directives',
              requiresFinancialOrMarketingAdminRole: true,
            },
            component: () => import('@/bundles/Marketing/views/MarketingMissionsAndDirectives.vue'),
          },
          {
            path: 'high-resolution-usage',
            name: 'high-resolution-usage',
            meta: {
              title: 'High Resolution Usage + Costs',
              requiresFinancialOrMarketingAdminRole: true,
            },
            component: () => import('@/bundles/Map/views/HighResolutionUsageView.vue'),
          },
          {
            path: 'external-provider-list',
            name: 'account-provider-list',
            meta: {
              title: 'External Providers',
              requiresFinancialOrMarketingAdminRole: true,
            },
            component: AccountProviderList,
          },
          {
            path: 'documents',
            name: 'documents', // :key="$route.fullPath" removed
            meta: {
              title: 'Marketing Documents',
              useFilterPanel: true,
            },
            component: () => import('@/components/account/view/Documents.vue'),
          },
          {
            path: 'training',
            name: 'training',
            meta: {
              title: 'Training',
            },
            component: () => import('@/components/account/view/Training.vue'),
          },
          {
            path: 'images',
            name: 'images',
            meta: {
              title: 'Images',
            },
            component: () => import('@/components/account/view/Images.vue'),
          },
          {
            path: 'account-details',
            name: 'account-details',
            meta: {
              title: 'Company Details',
              requiresAdminRole: true,
            },
            component: () => import('@/components/account/view/Details.vue'),
          },
          {
            path: 'settings',
            name: 'compensation-plans',
            meta: {
              title: 'Compensation Plans',
              requiresToken: true,
              requiresFinancialAdminRole: true,
            },
            component: () => import('@/components/account/view/CompensationPlans.vue'),
          },
          {
            path: 'setup',
            meta: {
              title: 'Setup',
            },
            component: () => import('@/components/account/view/Setup.vue'),
          },
          {
            path: 'spatial-settings',
            meta: {
              title: 'Spatial and Map Settings',
              requiresAdminRole: true,
            },
            name: 'administration-spatial-settings',
            component: () =>
              import('@/components/account/view/SpatialSettings.vue'),
          },
          {
            path: 'data-management',
            meta: {
              title: 'Data Management',
            },
            component: () => import('@/bundles/Administration/components/DataManagement/DataManagementRoot.vue'),
            children: [
              {
                path: 'contacts',
                meta: {
                  title: 'Import Contacts',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Contact/DataManagementCardContact.vue'),
                name: 'data-management-contacts',
              },
              {
                path: 'properties',
                meta: {
                  title: 'Import Properties',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Property/DataManagementCardProperty.vue'),
                name: 'data-management-properties',
              },
              {
                path: 'availabilities',
                meta: {
                  title: 'Import Availabilities',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Availability/DataManagementCardAvailability.vue'),
                name: 'data-management-availabilities',
              },
              {
                path: 'comparables',
                meta: {
                  title: 'Import Comparables',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Comparable/DataManagementCardComparable.vue'),
                name: 'data-management-comparables',
              },
              {
                path: 'notes',
                meta: {
                  title: 'Import Notes',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Note/DataManagementCardNote.vue'),
                name: 'data-management-notes',
              },
              {
                path: 'companies',
                meta: {
                  title: 'Import Companies',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Company/DataManagementCardCompany.vue'),
                name: 'data-management-companies',
              },
              {
                path: 'tasks',
                meta: {
                  title: 'Import Tasks',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Task/DataManagementCardTask.vue'),
                name: 'data-management-tasks',
              },
              {
                path: '',
                name: 'administration-data-management',
                meta: {
                  title: 'Data Management',
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/DataManagementNew.vue'),
              },
              {
                path: 'contacts/:id',
                meta: {
                  title: 'Import Contacts',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/views/Administration/ProcessImport'),
                name: 'data-management-contacts-process',
              },
              // saved sets import
              {
                path: 'contacts-set',
                meta: {
                  title: 'Import Contacts into Saved Set',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Contact/DataManagementCardContactSavedSet.vue'),
                name: 'data-management-contacts-set',
              },

              // import list
              {
                path: 'imports',
                meta: {
                  title: 'Imports',
                  requiresFinancialOrMarketingAdminRole: true,
                },
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/ImportListPage.vue'),
                name: 'data-management-imports',
              },

              // lease ingestion
              {
                path: 'lease-ingestion',
                meta: {
                  title: 'Lease Ingestion',
                },
                props: true,
                component: () => import('@/bundles/LeaseIngestion/components/LeaseIngestionQueuePage.vue'),
                name: 'data-management-lease-ingestion',
              },
              {
                path: 'lease-ingestion/:id',
                meta: {
                  title: 'Lease Ingestion',
                },
                props: true,
                component: () => import('@/bundles/LeaseIngestion/components/LeaseIngestionDetailPage.vue'),
                name: 'data-management-lease-ingestion-detail',
              },

              // availability ingestion
              {
                path: 'availability-ingestion',
                meta: {
                  title: 'Availability Ingestion',
                },
                props: true,
                component: () => import('@/bundles/AvailabilityIngestion/components/AvailabilityIngestionQueuePage.vue'),
                name: 'data-management-availability-ingestion',
              },
              {
                path: 'availability-ingestion/:id',
                meta: {
                  title: 'Availability Ingestion',
                },
                props: true,
                component: () => import('@/bundles/AvailabilityIngestion/components/AvailabilityIngestionDetailPage.vue'),
                name: 'data-management-availability-ingestion-detail',
              },
            ]
          },
          {
            path: 'data-management/properties/:id',
            meta: {
              title: 'Import Properties',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessPropertiesImport.vue'),
            name: 'data-management-properties-process',
          },
          {
            path: 'data-management/availabilities/:id',
            meta: {
              title: 'Import Availabilities',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessAvailabilitiesImport.vue'),
            name: 'data-management-availabilities-process',
          },
          {
            path: 'data-management/comparables/:id',
            meta: {
              title: 'Import Comparables',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessComparablesImport.vue'),
            name: 'data-management-comparables-process',
          },
          {
            path: 'data-management/notes/:id',
            meta: {
              title: 'Import Notes',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessNotesImport.vue'),
            name: 'data-management-notes-process',
          },
          {
            path: 'data-management/companies/:id',
            meta: {
              title: 'Import Companies',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/Company/ProcessCompaniesImport.vue'),
            name: 'data-management-companies-process',
          },
          {
            path: 'data-management/tasks/:id',
            meta: {
              title: 'Import Tasks',
              requiresFinancialOrMarketingAdminRole: true,
            },
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/Task/ProcessTasksImport.vue'),
            name: 'data-management-tasks-process',
          },
          {
            path: 'requests',
            meta: {
              title: 'Feature Requests and Bug Tracker'
            },
            name: 'administration-requests', // :key="$route.fullPath" removed
            component: () => import('@/views/Administration/RequestsTracker'),
          },
          {
            path: 'request/:id',
            meta: {
              title: 'Request'
            },
            name: 'administration-request-record',
            component: () => import('@/bundles/Requests/views/RequestView.vue'),
          },
          {
            path: 'export',
            meta: {
              title: 'Export',
            },
            component: () => import('@/views/Administration/DataExport'),
          },

          {
            path: 'members',
            name: 'members', // :key="$route.fullPath" removed
            meta: {
              title: 'Members and Teams',
              requiresToken: true,
              requiresFinancialAdminOrAdminRole: true,
            },
            component: Members
          },
          {
            path: 'account-settings',
            meta: {
              title: 'Settings',
              requiresAdminRole: true,
            },
            name: 'administration-account-settings',
            component: () => import('@/views/Administration/AccountSettings'),
          },
          {
            path: 'dynamic-data',
            meta: {
              title: 'Dynamic Data',
              requiresAdminRole: true,
            },
            name: 'administration-dynamic-data',
            component: () => import('@/views/Administration/DynamicData'),
          },
          {
            path: 'dealius-push',
            meta: {
              title: 'Dealius Push',
              requiresFinancialAdminRole: true,
            },
            name: 'administration-dealius-push',
            component: () => import('@/views/Administration/DealiusPush'),
          },
          // todo: Remove this after bee plugin testing
          {
            path: 'bee-plugin-testing',
            meta: {
              title: 'Bee Plugin Testing'
            },
            name: 'administration-bee-plugin',
            component: () => import('@/views/Administration/BeePlugin'),
          },
          {
            path: 'listing-verification',
            name: 'ListingVerificationSettings',
            meta: {
              title: 'Listing Verification Settings',
              requiresFinancialOrMarketingAdminRole: true,
            },
            component: () => import('@/views/Administration/ListingVerification'),
          },
          {
            path: 'mcp-roles',
            name: 'administration-mcp-roles',
            meta: {
              title: 'MCP Roles',
              requiresSuperAdminRole: true,
            },
            component: () => import('@/bundles/Administration/components/McpRoles/McpRolesView.vue'),
          }
        ]
      },
      {
        path: 'saved-sets',
        name: 'sets', // :key="$route.fullPath" removed
        component: SavedSets,
        meta: {
          title: 'Saved Sets',
        },
      },
      {
        path: 'member/tile',
        name: 'member-tile',
        component: () => import('@/components/members/MemberTile.vue'),
        props: true,
        meta: {
          title: 'Member Tile',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'sign-list',
        name: 'sign-list', // :key="$route.fullPath" removed
        component: () => import('@/bundles/SignIn/views/SignListView.vue'),
        props: true,
        meta: {
          title: 'Sign List',
        },
      },
      {
        path: 'sign/:id',
        name: 'sign', // :key="$route.fullPath" removed
        component: () => import('@/bundles/SignIn/views/SignRecordView.vue'),
        props: true,
        meta: {
          title: 'Sign',
        },
      },
      {
        path: 'order-approve/:id',
        name: 'order-approve',
        component: () => import('@/components/workorder/OrderApproval.vue'),
        props: true,
        meta: {
          title: 'Order Approval',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'access',
        name: 'access-denied',
        component: AccessDenied,
        meta: {
          title: 'Access Denied',
        },
      },
      {
        path: 'availability/:id/print',
        component: () => import('@/views/Availability/AvailabilityPrint.vue'),
        name: 'availability-print',
        props: true,
        meta: {
          title: 'Availability Print',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'comparable/:id/print',
        component: ComparablePrint,
        name: 'comparable-print',
        meta: {
          title: 'Comparable Print',
          layout: LayoutEnum.Blank,
        },
      },
      {
        path: 'comparable/:id',
        name: 'comparable',
        component: Comparable,
        props: true,
        meta: {
          title: 'Comparable',
        },
      },
      {
        component: UserSettingsRootViewNew,
        path: 'settings',
        children: [
          {
            path: '',
            name: 'user-profile', // :key="$route.fullPath" removed
            meta: {
              title: 'Profile',
              allowClientAccess: true,
              preventCancelRequestOnLeave: true,
            },
            component: UserProfile,
          },
          {
            path: 'security',
            name: 'user-security', // :key="$route.fullPath" removed
            meta: {
              title: 'Security',
              allowClientAccess: true,
            },
            component: UserSecurity,
          },
          {
            path: 'permissions',
            name: 'user-permissions', // :key="$route.fullPath" removed
            meta: {
              title: 'Permissions',
            },
            component: UserPermissions,
          },
          {
            path: 'user-settings',
            name: 'user-settings', // :key="$route.fullPath" removed
            component: UserSettings,
            meta: {
              title: 'Settings',
            },
            props: true,
          },
          {
            path: 'main',
            name: 'user-main', // :key="$route.fullPath" removed
            meta: {
              title: 'Main Settings',
            },
            component: UserMain,
            children: [
              {
                path: 'dashboard',
                name: 'settings-dashboard', // :key="$route.fullPath" removed
                meta: {
                  title: 'Dashboard Settings',
                },
                component: DashboardOrder,
              },
              {
                path: 'notifications',
                name: 'settings-notifications', // :key="$route.fullPath" removed
                meta: {
                  title: 'Notifications Settings',
                },
                component: NotificationsSettings,
              },
              {
                path: 'opex',
                name: 'settings-opex', // :key="$route.fullPath" removed
                meta: {
                  title: 'Opex Settings',
                },
                component: OpexSettings,
              },
              {
                path: 'visualizations',
                name: 'settings-visualizations',
                meta: {
                  title: 'Visualizations Settings',
                },
                component: VisualizationSettings,
              },
              {
                path: 'permissions-settings',
                name: 'permissions-settings',
                meta: {
                  title: 'Permission Settings',
                },
                component: PermissionSettings,
              },
            ]
          },
          {
            path: 'subscription',
            name: 'user-subscription', // :key="$route.fullPath" removed
            meta: {
              title: 'Subscription',
            },
            component: UserSubscription,
          },
          {
            path: 'apps',
            name: 'user-apps', // :key="$route.fullPath" removed
            component: UserApps,
            meta: {
              title: 'Apps',
            },
            props: true,
          },
          {
            path: 'downloads',
            name: 'user-downloads', // :key="$route.fullPath" removed
            component: () => import('@/bundles/User/views/UserDownloadsView.vue'),
            meta: {
              title: 'My Downloads',
            },
            props: true,
          },
          {
            path: 'exports',
            name: 'exports', // :key="$route.fullPath" removed
            component: () => import('@/bundles/Export/components/ExportListTable.vue'),
            meta: {
              title: 'My Exports',
            },
            props: true,
          },
          {
            path: 'reports',
            name: 'reports', // :key="$route.fullPath" removed
            component: () => import('@/bundles/Reports/components/ReportListTable.vue'),
            meta: {
              title: 'My Reports',
            },
            props: true,
          },
          {
            path: 'focus-area',
            name: 'settings-focus-area', // :key="$route.fullPath" removed
            meta: {
              title: 'Focus Area',
            },
            component: FocusAreaSettings,
          },
          {
            path: 'ai-keys',
            name: 'settings-ai-keys', // :key="$route.fullPath" removed
            meta: {
              title: 'AI Keys',
            },
            component: () => import('@/bundles/Settings/components/AiKeysSettings.vue'),
          }
        ],
      },
      {
        path: 'marketing',
        name: 'marketing', // :key="$route.fullPath" removed
        component: MarketingDashboard,
        props: true,
        meta: {
          title: 'Marketing',
        },
      },
      {
        path: 'marketing/press-releases',
        name: 'PressReleases', // :key="$route.fullPath" removed
        component: PressReleases,
        meta: {
          title: 'Press Releases',
        },
        props: true,
      },
      {
        name: 'PressRelease',
        path: 'marketing/press-release/:id',
        component: PressRelease,
        props: true,
        meta: {
          title: 'Press Release',
        },
      },
      {
        name: 'ProcessPressRelease',
        path: 'marketing/press-release/:id/approve/:approveKey',
        component: ProcessPressRelease,
        props: true,
        meta: {
          title: 'Process Press Release',
        },
      },
      ...MarketingRoutes,
      ...ReportRoutes,
      {
        path: '*', // handling all not existing routes and routes with id in url
        meta: {
          layout: LayoutEnum.Blank,
        },
        beforeEnter: (to, from, next) => {
          redirectHandler(to, next, store, true);
        }
      },
    ],
  },
]

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (location) {
  return originalPush.call(this, location).catch((err) => err)
}

Vue.use(VueRouter)

let router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior (to, from, savedPosition) {
    // A query/hash-only change (same path) must not jump the page to the top.
    if (to.path === from.path) {
      return false
    }
    return { x: 0, y: 0 }
  }
})

export const DEFAULT_TITLE = 'Slick Cactus';

router.beforeEach((to, from, next) => {
  if (window.location.hash) { // handle hash-to-history redirect before any guards e.g. /#/3/occupiers -> /3/occupiers
    const pathWithoutHash = window.location.hash.slice(1); // Extract the hash path (without the '#')

    if (pathWithoutHash && pathWithoutHash !== '/') { // Check if there's an actual path after the hash
      // clear the hash from the browser URL before calling next().
      // without this, window.location.hash still contains the old value. when next() synchronously triggers the next beforeEach cycle, causing an infinite redirect loop.
      window.history.replaceState(null, '', pathWithoutHash);
      next({ path: pathWithoutHash, replace: true });
      return;
    }
  }

  let result = requireRegularUser(to, from, next, store);
  if (to.meta?.requiresTokenOrTemporaryToken) {
    result = requiresTokenOrTemporaryTokenGuard(to, from, next, store);
  }

  if (to?.meta?.requiresTokenAbsence) {
    result = requiresTokenAbsenceGuard(to, from, next, store);
  }

  if (to.matched.some(record => record.meta?.requiresToken)) {
    result = requiresTokenGuard(to, from, next, store);
  }

  if (to.meta?.requiresRedirect) {
    result = requiresRedirectGuard(to, from, next, store);
  }

  if (to.meta?.requiresAdminToken) {
    result = requiresAdminTokenGuard(to, from, next, store);
  }

  if (to.meta?.requiresFinancialAdminRole) {
    result = requiresFinancialAdminRoleGuard(to, from, next, store);
  }

  if (to.meta?.requiresFinancialOrMarketingAdminRole) {
    result = requiresFinancialOrMarketingAdminRoleGuard(to, from, next, store);
  }

  if (to.meta?.requiresSuperAdminRole) {
    result = requiresSuperAdminRoleGuard(to, from, next, store);
  }

  if (to.meta?.requiresFinancialAdminOrAdminRole) {
    result = requiresFinancialAdminOrAdminRoleGuard(to, from, next, store);
  }

  if (to.meta?.requiresAdminRole) {
    result = requiresAdminRoleGuard(to, from, next, store);
  }

  if (result) {
    result = requiresPasswordChangeGuard(to, from, next, store);
  }

  if (result) {
    store.dispatch(RootActions.CANCEL_PENDING_REQUESTS);
    next();
  }
})

router.afterEach((to, from) => {
  // this is a hack to fix the issue with the title not being updated with history mode (need to have it in future)
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  Vue.nextTick(() => {
    document.title = to.meta.title ? `${to.meta.title} - ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  });
});

export default router
